import { b2Url, isB2Live } from '../config/backblaze';

export interface MotorsportPhoto {
  id: string;
  /** Filename inside the gallery folder on Backblaze */
  filename: string;
  alt: string;
  caption?: string;
}

export interface MotorsportGallery {
  id: string;
  /** Folder name on Backblaze (upload photos here) */
  folder: string;
  title: string;
  description: string;
  location: string;
  date: string;
  photos: MotorsportPhoto[];
}

/**
 * Galleries backed by Backblaze B2.
 * Folder layout (under VITE_B2_ROOT_FOLDER):
 *
 *   asian-le-mans-2025/  f4/  endurance-dubai/  endurance-auh/  endurance-malaysia/ (Malaysia Le Mans)  liwa-2025/  motocross/
 */

function galleryPhotos(
  prefix: string,
  count: number,
  captions: string[] = []
): MotorsportPhoto[] {
  const photos: MotorsportPhoto[] = [
    {
      id: `${prefix}-cover`,
      filename: 'cover.jpg',
      alt: `${prefix} cover`,
      caption: captions[0] || 'Cover'
    }
  ];
  for (let i = 1; i < count; i++) {
    const n = String(i).padStart(2, '0');
    photos.push({
      id: `${prefix}-${n}`,
      filename: `${n}.jpg`,
      alt: `${prefix} ${i}`,
      caption: captions[i] || `Shot ${i}`
    });
  }
  return photos;
}

export const motorsportGalleries: MotorsportGallery[] = [
  {
    id: 'asian-le-mans-2025',
    folder: 'asian-le-mans-2025',
    title: 'Asian Le Mans Series',
    description:
      'Intensity and speed — endurance GT and prototype action from the Asian Le Mans weekend.',
    location: 'Dubai / Sepang',
    date: '2026',
    photos: galleryPhotos('alm', 20)
  },
  {
    id: 'f4',
    folder: 'f4',
    title: 'Formula 4',
    description: 'Single-seater pace up close — open-wheel racing from the F4 weekend.',
    location: 'Dubai Autodrome',
    date: '2026',
    photos: galleryPhotos('f4', 15)
  },
  {
    id: 'endurance-dubai',
    folder: 'endurance-dubai',
    title: 'Endurance Dubai',
    description: 'Long-stint endurance racing at Virage — night runs, pit rhythm, and desert air.',
    location: 'Virage, Dubai',
    date: '2026',
    photos: galleryPhotos('virage', 16)
  },
  {
    id: 'endurance-auh',
    folder: 'endurance-auh',
    title: 'Endurance Race Abu Dhabi',
    description: 'Endurance racing under Abu Dhabi skies — pace, night stints, and circuit atmosphere.',
    location: 'Abu Dhabi, UAE',
    date: '2026',
    photos: galleryPhotos('auh', 31)
  },
  {
    id: 'endurance-malaysia',
    folder: 'endurance-malaysia',
    title: 'Malaysia Le Mans',
    description: 'Asian Le Mans endurance at Sepang — GT and prototype pace under Malaysian heat.',
    location: 'Sepang, Malaysia',
    date: '2025',
    photos: galleryPhotos('mys', 25)
  },
  {
    id: 'liwa-2025',
    folder: 'liwa-2025',
    title: 'Liwa 2025',
    description: 'Desert motorsport energy — sand, speed, and UAE landscape.',
    location: 'Liwa, UAE',
    date: '2025',
    photos: galleryPhotos('liwa', 9, [
      'Desert stage',
      'Dune line',
      'Dust trail',
      'Golden hour',
      'Service stop',
      'Sand track',
      'Open desert',
      'Side-by-side',
      'Sunset run'
    ])
  },
  {
    id: 'motocross',
    folder: 'motocross',
    title: 'Motocross',
    description: 'Air time, berms, and grit — riders pushing through technical terrain.',
    location: 'Various',
    date: '2024–2025',
    photos: galleryPhotos('mx', 9, [
      'Moto Triangle',
      'Trail ride',
      'Desert line',
      'Corner exit',
      'Whoops',
      'Air time',
      'Berm',
      'Start gate',
      'Finish'
    ])
  }
];

/** SVG placeholder used until Backblaze uploads are live */
export function placeholderPhotoUrl(label: string, caption?: string): string {
  const title = encodeURIComponent(label.slice(0, 28));
  const sub = encodeURIComponent((caption || 'Photo coming soon').slice(0, 36));
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#0f172a"/>
      <stop offset="55%" stop-color="#134e4a"/>
      <stop offset="100%" stop-color="#1e293b"/>
    </linearGradient>
  </defs>
  <rect width="1200" height="800" fill="url(#g)"/>
  <rect x="48" y="48" width="1104" height="704" rx="24" fill="none" stroke="#2dd4bf" stroke-opacity="0.35" stroke-width="2"/>
  <text x="600" y="360" text-anchor="middle" fill="#5eead4" font-family="system-ui,sans-serif" font-size="42" font-weight="600">${title}</text>
  <text x="600" y="420" text-anchor="middle" fill="#94a3b8" font-family="system-ui,sans-serif" font-size="24">${sub}</text>
  <text x="600" y="720" text-anchor="middle" fill="#64748b" font-family="system-ui,sans-serif" font-size="18">Backblaze B2 template · upload to replace</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function resolvePhotoUrl(gallery: MotorsportGallery, photo: MotorsportPhoto): string {
  if (isB2Live()) {
    const url = b2Url(gallery.folder, photo.filename);
    if (url) return url;
  }
  return placeholderPhotoUrl(gallery.title, photo.caption || photo.alt);
}

export function resolveGalleryCover(gallery: MotorsportGallery): string {
  const cover = gallery.photos[0];
  if (!cover) return placeholderPhotoUrl(gallery.title);
  return resolvePhotoUrl(gallery, cover);
}

export function resolveGalleryPhotos(gallery: MotorsportGallery): string[] {
  return gallery.photos.map((p) => resolvePhotoUrl(gallery, p));
}
